import { useEffect, useState } from "react";
import { useAuth } from "@/providers/auth-provider";
import { getTeamSelection } from "@/services/battle/get-team-selection";
import { joinBattle } from "@/services/battle/join-battle";
import { TeamSelectionOutput } from "types/web-app";
import { StandardFrame } from "@/components/ui/frames/standard-frame";
import { capitalizeFirstLetter } from "utils/string-utils";
import { StrokedText } from "@/components/ui/text/stroked-text";
import classNames from "classnames";
import { PokemonSprite } from "@/components/pokemon/pokemon-sprite";
import { PokemonTypeBadge } from "@/components/pokemon/pokemon-type-badge";
import { getPokeData } from "game/data/pokemon/evolution";
import { PokeType } from "types/pokemon/poke-type";
import { Alert } from "@/components/alert/alert";

export const PickPokemonView = () => {
  const { getAccessToken } = useAuth();
  const [team, setTeam] = useState<TeamSelectionOutput | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Parse battle_id and chat_id from Telegram start_param
  const startParam =
    (window as any).Telegram?.WebApp?.initDataUnsafe?.start_param || "";
  const [battleType, battleId, encodedChatId] = startParam.split("_");
  const chatId = encodedChatId?.replace("m", "-");

  useEffect(() => {
    const fetchTeam = async () => {
      const token = getAccessToken();
      if (!token) return;

      try {
        const res = await getTeamSelection(token);
        if (res.ok && res.data) {
          setTeam(res.data);
        } else {
          setError(res.message || "Failed to load team");
        }
      } catch (err) {
        console.error(err);
        setError("An error occurred while loading your team.");
      } finally {
        setLoading(false);
      }
    };

    fetchTeam();
  }, [getAccessToken]);

  const handleSelect = async (pokemonId: number) => {
    if (submitting !== null || !battleId || !chatId) return;

    const token = getAccessToken();
    if (!token) return;

    setSubmitting(pokemonId);
    setError(null);

    await new Promise((resolve) => setTimeout(resolve, 2000));

    setSuccess(true);

    if (battleType !== "joinS" && battleType !== "joinG") {
      setError("Invalid battle type");
      setSubmitting(null);
      return;
    }

    const battle_type: "single" | "group" =
      battleType === "joinS" ? "single" : "group";

    try {
      const res = await joinBattle(token, {
        battle_id: battleId,
        chat_id: chatId,
        selected_pokemon_id: pokemonId.toString(),
        battle_type,
      });

      if (res.ok) {
        setSuccess(true);
      } else {
        setError(res.message || "Failed to join battle");
        setSubmitting(null);
      }
    } catch (err) {
      console.error(err);
      setError("An error occurred while joining the battle.");
      setSubmitting(null);
    }
  };

  const handleClose = () => {
    (window as any).Telegram?.WebApp?.close();
  };

  if (loading) {
    return (
      <div className="relative w-full min-h-screen bg-pk-night-2 backdrop-blur-[3px] flex items-center justify-center p-4">
        <StandardFrame variant="blue" className="">
          <div className="flex flex-col items-center justify-center py-8">
            <div className="w-12 h-12 border-4 border-pk-blue-4 border-t-transparent rounded-full animate-spin" />
            <p className="mt-4 text-pk-gray-4">Loading your team...</p>
          </div>
        </StandardFrame>
      </div>
    );
  }

  if (success) {
    return (
      <div className="relative w-full min-h-screen bg-pk-night-2 backdrop-blur-[3px] flex items-center justify-center p-4">
        <StandardFrame variant="green" className="">
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <div className="text-6xl mb-4">✅</div>
            <StrokedText strokeWidth={10} fontSize={24}>
              SUCCESS!
            </StrokedText>
            <p className="text-pk-gray-4 mt-2">
              Your Pokemon will join the battle shortly.
            </p>
            <p className="text-pk-gray-5 text-sm mt-4">
              You can close this window now.
            </p>

            <button
              onClick={handleClose}
              className="mt-8 cursor-pointer text-pk-gray-4 text-lg font-bold hover:text-white transition-colors text-center uppercase tracking-widest"
            >
              — OK —
            </button>
          </div>
        </StandardFrame>
      </div>
    );
  }

  return (
    <div className="relative w-full min-h-screen bg-pk-night-1 backdrop-blur-[3px] overflow-y-auto p-4">
      <div className="flex flex-col w-full max-w-md mx-auto py-8">
        <div className="text-center mb-6">
          <StrokedText strokeWidth={12} fontSize={28}>
            SELECT POKÉMON
          </StrokedText>
          <p className="text-pk-gray-4 text-sm mt-2">
            Choose which Pokémon will join the battle.
          </p>
        </div>

        {error && (
          <div className="bg-red-500/20 border border-red-500/50 text-red-200 p-3 rounded-lg text-sm text-center mb-6">
            {error}
          </div>
        )}

        <StandardFrame variant="black" className="">
          <div className="grid grid-cols-4 gap-3">
            {team?.slots.map((slot) => {
              const pokemon = slot.pokemon;
              const pokeData = getPokeData(pokemon?.base_pokemon_name || "");
              const isBusy = slot.busy;
              const isSubmitting = submitting === pokemon?.id;
              const isSubmittingOther =
                submitting !== null && submitting !== pokemon?.id;

              return (
                <button
                  key={slot.position}
                  disabled={!pokemon || isBusy || submitting !== null}
                  onClick={() => pokemon && handleSelect(pokemon.id)}
                  className={classNames(
                    "relative flex flex-col bg-pk-night-2 cursor-pointer items-center p-3 border-pk-blue-4/30 rounded-xl border-2 transition-all duration-200",
                    {
                      "  hover:border-pk-blue-4 active:scale-95":
                        pokemon && !isBusy && submitting === null,
                      "bg-pk-night-3/50 border-pk-gray-3/20 opacity-50 cursor-not-allowed":
                        !pokemon || (isBusy && !isSubmitting),
                      "scale-150 z-99": isSubmitting,
                    },
                  )}
                >
                  {pokemon ? (
                    <>
                      <PokemonSprite
                        pokemon={pokemon}
                        style={{ scale: 2 }}
                        showShadow
                      />
                      <div className="text-center mt-auto">
                        {pokeData?.types && pokeData?.types.length > 0 && (
                          <div className="flex flex-col justify-center gap-1 mt-1 mb-1">
                            {pokeData.types
                              .filter(
                                (type): type is PokeType => type !== undefined,
                              )
                              .map((type) => (
                                <div
                                  className="text-[10px] px-2 py-0.5 rounded-md uppercase"
                                  style={{
                                    backgroundColor: `var(--color-pk-${type.toLowerCase()})`,
                                    textShadow: `-2px 2px 1px rgba(0, 0, 0, 0.95)`,
                                  }}
                                >
                                  {type}
                                </div>
                              ))}
                          </div>
                        )}

                        <div className="text-white font-bold text-xs truncate w-full px-1">
                          {pokemon.name
                            ? pokemon.name
                            : capitalizeFirstLetter(pokemon.base_pokemon_name)}
                        </div>
                        <div className="text-pk-blue-4 text-[10px] font-bold">
                          Lv. {pokemon.level}
                        </div>
                      </div>

                      {isBusy && (
                        <div className="absolute inset-0 bg-black/70 rounded-lg flex items-center justify-center">
                          <span className="text-white text-sm font-black tracking-tighter bg-red-600 px-2 py-0.5 rounded uppercase">
                            BUSY
                          </span>
                        </div>
                      )}

                      {isSubmittingOther && (
                        <div className="absolute inset-0 bg-pk-night-2/70 rounded-lg flex items-center justify-center"></div>
                      )}

                      {isSubmitting && (
                        <div className="absolute inset-0  rounded-lg flex items-center justify-center">
                          <div className="w-6 h-6 border-2 border-pk-blue-4 border-t-transparent rounded-full animate-spin" />
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="flex flex-col items-center justify-center h-24">
                      <span className="text-pk-gray-3 text-2xl">+</span>
                      <span className="text-pk-gray-4 text-[10px] font-bold">
                        EMPTY
                      </span>
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </StandardFrame>

        <button
          onClick={handleClose}
          className="mt-8 cursor-pointer text-pk-gray-4 text-xs font-bold hover:text-white transition-colors text-center uppercase tracking-widest"
        >
          — CANCEL —
        </button>
      </div>
    </div>
  );
};
